// HotelSelectorMany.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Axios from "axios";

import {
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import CheckShow from "./shared/CheckShow";

import Colours from "../helpers/colours";
import Icons from "../helpers/icons";
import {
    ARO_ADMIN,
    ARO_ALL,
    getHotelName,
    HOTELS_INFO,
    URLAPI,
} from "../configuration";
import { ITEM_HEIGHT } from "./FiltersReport";
import { selectHotels } from "../features/hotels/hotelsApi";

const HOTEL_RATINGS = [1, 2, 3, 4, 5];

const getHotelRating = (hotel) => {
    const rating = Number(hotel?.rating ?? hotel?.Rating);
    return Number.isFinite(rating) ? rating : null;
};

const HotelSelectorMany = ({
    selectedHotels = [],
    setSelectedHotels = () => {},
    // If provided, hides the trigger button and lets the parent control open state
    open = undefined,
    onClose = undefined,
    // Optional action buttons — only rendered when the callback is provided
    onCancel = undefined,
    cancelLabel = "Cancel",
    onSave = undefined,
    saveLabel = "Save",
    saveDisabled = false,
    AccountManager = false,
    disableCurrentHotel = false,
    allowEmptySelection = false,
    showStarsFilter = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { enqueueSnackbar } = useSnackbar();

    const { user } = useSelector((state) => state.auth);
    const { id: hotelID = "default" } = useSelector((state) => state.hotelID);
    const authToken = user?.jwt ?? "";
    const { data: hotels = [] } = useSelector(selectHotels);

    const hotelsFromApiById = useMemo(() => {
        const hotelList = Array.isArray(hotels) ? hotels : (hotels?.data ?? []);

        return Object.fromEntries(
            hotelList.map((hotel) => [String(hotel.HotelID), hotel]),
        );
    }, [hotels]);

    const getHotelInfo = (hotel) => {
        const apiHotel = hotelsFromApiById[String(hotel?.id)];

        return {
            ...hotel,
            name: getHotelName(hotel?.id, hotels),
            rating: apiHotel?.Rating ?? hotel?.rating ?? hotel?.Rating ?? null,
            countryName: apiHotel?.CountryName ?? hotel?.countryName,
            city: apiHotel?.City ?? hotel?.city,
            currencyCode: apiHotel?.CurrencyCode ?? hotel?.currencyCode,
            currencySymbol: apiHotel?.CurrencySymbol ?? hotel?.currencySymbol,
        };
    };

    const [accountManagers, setAccountManagers] = useState([]);
    const [hotelManagersRows, setHotelManagersRows] = useState([]);
    const [selectedAccountManagerId, setSelectedAccountManagerId] =
        useState("");
    const [expandAccountManagers, setExpandAccountManagers] = useState(false);
    const [expandHotels, setExpandHotels] = useState(true);

    const [searchText, setSearchText] = useState("");
    const [selectedRatings, setSelectedRatings] = useState([]);

    const [options, setOptions] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);

    const isExternallyControlled = open !== undefined;
    const dialogOpen = isExternallyControlled ? open : openDialog;

    const [draftSelectedHotels, setDraftSelectedHotels] =
        useState(selectedHotels);

    useEffect(() => {
        if (dialogOpen) {
            setDraftSelectedHotels(selectedHotels);
        }
    }, [dialogOpen, selectedHotels]);

    const allFilteredSelected =
        filteredOptions.length > 0 &&
        filteredOptions.every((o) => draftSelectedHotels.includes(o.id));
    const someFilteredSelected =
        filteredOptions.some((o) => draftSelectedHotels.includes(o.id)) &&
        !allFilteredSelected;

    useEffect(() => {
        if (!AccountManager || !authToken) {
            setAccountManagers([]);
            setHotelManagersRows([]);
            setSelectedAccountManagerId("");
            return;
        }

        let cancelled = false;

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        };

        const unwrapList = (resp) => {
            const payload = resp?.data?.data ?? resp?.data ?? null;

            if (Array.isArray(payload)) return payload;
            if (Array.isArray(payload?.data)) return payload.data;

            return [];
        };

        const run = async () => {
            try {
                const [managersResp, relationsResp] = await Promise.all([
                    Axios.get(`${URLAPI}/account-managers`, { headers }),
                    Axios.get(`${URLAPI}/hotels/managers`, { headers }),
                ]);

                const managers = unwrapList(managersResp);
                const relations = unwrapList(relationsResp);

                const sortedManagers = [...managers].sort((a, b) =>
                    String(a?.manager_name ?? "").localeCompare(
                        String(b?.manager_name ?? ""),
                    ),
                );

                if (!cancelled) {
                    setAccountManagers(sortedManagers);
                    setHotelManagersRows(
                        Array.isArray(relations) ? relations : [],
                    );
                }
            } catch (e) {
                console.error(
                    "Failed to load account managers / relations:",
                    e,
                );

                if (!cancelled) {
                    setAccountManagers([]);
                    setHotelManagersRows([]);
                    setSelectedAccountManagerId("");
                }
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [AccountManager, authToken]);

    useEffect(() => {
        if (user && user.rolesHotels) {
            if ((user.rolesHotels ?? []).includes(-1)) {
                // Remove key 14
                const filteredHotels = Object.entries(HOTELS_INFO)
                    .filter(([key]) => key !== "14")
                    .map(([, value]) => getHotelInfo(value));

                setOptions(filteredHotels);
            } else {
                setOptions(
                    user.rolesHotels
                        .filter((h) => h in HOTELS_INFO)
                        .map((id) => getHotelInfo(HOTELS_INFO[id])),
                );
            }
        } else {
            setOptions([]);
        }
    }, [user, hotelsFromApiById]);

    useEffect(() => {
        const lowerCaseSearchText = searchText.toLowerCase();

        const results = options.filter((option) => {
            const matchesSearch =
                searchText === "" ||
                option.name.toLowerCase().includes(lowerCaseSearchText) ||
                (option.simplified_name ?? "")
                    .toLowerCase()
                    .includes(lowerCaseSearchText) ||
                option.id
                    .toString()
                    .toLowerCase()
                    .includes(lowerCaseSearchText);

            const matchesRating =
                selectedRatings.length === 0 ||
                selectedRatings.includes(getHotelRating(option));

            return matchesSearch && matchesRating;
        });

        setFilteredOptions(results);
    }, [options, searchText, selectedRatings]);

    const userCanSeeHotel = (id) => {
        return (
            user?.rolesHotels?.includes(-1) ||
            user?.rolesHotels?.includes(Number(id))
        );
    };

    const optionsById = useMemo(
        () =>
            Object.fromEntries(
                options.map((hotel) => [String(hotel.id), hotel]),
            ),
        [options],
    );

    const accountManagerHotelsById = useMemo(() => {
        const result = {};

        hotelManagersRows.forEach((row) => {
            const managerId = String(row?.manager_id ?? "");
            const id = Number(row?.hotel_id);

            if (
                managerId &&
                Number.isFinite(id) &&
                id in HOTELS_INFO &&
                userCanSeeHotel(id)
            ) {
                result[managerId] = [
                    ...new Set([...(result[managerId] ?? []), id]),
                ];
            }
        });

        return result;
    }, [hotelManagersRows, user?.rolesHotels]);

    const ratingFilteredAccountManagerHotelsById = useMemo(() => {
        return Object.fromEntries(
            Object.entries(accountManagerHotelsById).map(([managerId, ids]) => [
                managerId,
                ids.filter((id) => {
                    if (selectedRatings.length === 0) return true;

                    return selectedRatings.includes(
                        getHotelRating(optionsById[String(id)]),
                    );
                }),
            ]),
        );
    }, [accountManagerHotelsById, optionsById, selectedRatings]);

    useEffect(() => {
        if (selectedAccountManagerId) {
            setDraftSelectedHotels(
                ratingFilteredAccountManagerHotelsById[
                    String(selectedAccountManagerId)
                ] ?? [],
            );
            return;
        }

        if (selectedRatings.length > 0) {
            setDraftSelectedHotels((prev) =>
                prev.filter((id) =>
                    selectedRatings.includes(
                        getHotelRating(optionsById[String(id)]),
                    ),
                ),
            );
        }
    }, [
        selectedAccountManagerId,
        ratingFilteredAccountManagerHotelsById,
        selectedRatings,
        optionsById,
    ]);

    const handleSelectAccountManager = (managerId) => {
        const managerHotels = accountManagerHotelsById[String(managerId)] ?? [];
        const visibleManagerHotels =
            ratingFilteredAccountManagerHotelsById[String(managerId)] ?? [];

        const isSameAccountManager =
            String(selectedAccountManagerId) === String(managerId) ||
            (visibleManagerHotels.length > 0 &&
                visibleManagerHotels.length === draftSelectedHotels.length &&
                visibleManagerHotels.every((id) =>
                    draftSelectedHotels.includes(id),
                ));

        if (isSameAccountManager) {
            setSelectedAccountManagerId("");
            setDraftSelectedHotels((prev) =>
                prev.filter((id) => !managerHotels.includes(id)),
            );
            return;
        }

        setSelectedAccountManagerId(String(managerId));
        setDraftSelectedHotels([...new Set(visibleManagerHotels)]);
    };

    const handleToggleHotel = (id) => {
        if (disableCurrentHotel && id === hotelID) return;

        setSelectedAccountManagerId("");

        setDraftSelectedHotels((prev) =>
            prev.includes(id) ? prev.filter((h) => h !== id) : [...prev, id],
        );
    };

    const handleSelectAll = (checked) => {
        setSelectedAccountManagerId("");

        const ids = filteredOptions
            .map((o) => o.id)
            .filter((id) => !(disableCurrentHotel && id === hotelID));

        if (checked) {
            setDraftSelectedHotels((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.add(id));
                return Array.from(next);
            });
        } else {
            setDraftSelectedHotels((prev) =>
                prev.filter((id) => !ids.includes(id)),
            );
        }
    };

    const handleReset = () => {
        setSelectedAccountManagerId("");
        setDraftSelectedHotels([]);
    };

    const closeDialog = () => {
        if (!isExternallyControlled) setOpenDialog(false);
        setSearchText("");
        setSelectedRatings([]);
        setSelectedAccountManagerId("");
    };
    const applyAndClose = () => {
        if (!allowEmptySelection && draftSelectedHotels.length === 0) {
            enqueueSnackbar("Select at least one hotel", {
                variant: "warning",
            });
            setSelectedAccountManagerId("");
            setDraftSelectedHotels(selectedHotels);
            onClose?.();
            closeDialog();
            return;
        }

        setSelectedHotels(draftSelectedHotels);
        onClose?.();
        closeDialog();
    };

    const handleClickX = () => {
        applyAndClose();
    };

    const handleCancel = () => {
        setDraftSelectedHotels(selectedHotels);
        onCancel?.();
        closeDialog();
    };

    const effectiveSaveDisabled =
        typeof saveDisabled === "function"
            ? saveDisabled(draftSelectedHotels.length)
            : saveDisabled;

    const effectiveSaveLabel =
        typeof saveLabel === "function"
            ? saveLabel(draftSelectedHotels.length)
            : saveLabel;

    const handleSave = () => {
        if (!allowEmptySelection && draftSelectedHotels.length === 0) {
            enqueueSnackbar("Select at least one hotel", {
                variant: "warning",
            });
            return;
        }

        setSelectedHotels(draftSelectedHotels);
        onSave?.(draftSelectedHotels);
        closeDialog();
    };

    const displayedOptions = useMemo(() => {
        const filteredIds = new Set(
            filteredOptions.map((hotel) => String(hotel.id)),
        );

        return options.filter(
            (hotel) =>
                !hotel.isChild &&
                (filteredIds.has(String(hotel.id)) ||
                    hotel.parent?.some((id) => filteredIds.has(String(id)))),
        );
    }, [options, filteredOptions]);

    const ListItemComponent = ({ data }) => {
        const [expandChild, setExpandChild] = useState(true);
        const isCurrentHotel = disableCurrentHotel && data.id === hotelID;
        const rating = getHotelRating(data);

        return (
            <>
                <ListItem disableGutters key={data.id}>
                    <ListItemButton
                        disabled={isCurrentHotel}
                        onClick={() => {
                            handleToggleHotel(data.id);
                        }}
                    >
                        <Checkbox
                            checked={draftSelectedHotels.includes(data.id)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleToggleHotel(data.id)}
                        />
                        <ListItemAvatar>
                            <Avatar
                                alt={`Hotel Logo ${data.name}`}
                                src={data.img}
                            />
                        </ListItemAvatar>
                        <CheckShow
                            rolesThatCanSee={ARO_ADMIN}
                            alternative={
                                <ListItemText
                                    primary={
                                        isMobile
                                            ? data.name.substring(0, 20) + "..."
                                            : data.name
                                    }
                                />
                            }
                        >
                            <ListItemText
                                secondary={
                                    rating === null
                                        ? data.id
                                        : `${data.id} · ${rating}★`
                                }
                                primary={
                                    isMobile
                                        ? data.name.substring(0, 20) + "..."
                                        : data.name
                                }
                            />
                        </CheckShow>
                    </ListItemButton>
                    {data.hasOwnProperty("parent") && (
                        <Button
                            onClick={() => {
                                setExpandChild(!expandChild);
                            }}
                            sx={{ color: Colours.transparentBubble }}
                        >
                            {expandChild ? (
                                <Icons.ArrowDown />
                            ) : (
                                <Icons.ArrowUp />
                            )}
                        </Button>
                    )}
                </ListItem>
                {data.hasOwnProperty("parent") && (
                    <Collapse in={expandChild} timeout="auto" unmountOnExit>
                        <List>
                            {data.parent
                                .filter(
                                    (id) =>
                                        user.rolesHotels.includes(-1) ||
                                        user.rolesHotels.includes(id),
                                )
                                .filter(
                                    (id) =>
                                        searchText === "" ||
                                        filteredOptions.some(
                                            (hotel) =>
                                                String(hotel.id) ===
                                                    String(data.id) ||
                                                String(hotel.id) === String(id),
                                        ),
                                )
                                .map((id) => getHotelInfo(HOTELS_INFO[id]))
                                .filter(
                                    (subHotel) =>
                                        selectedRatings.length === 0 ||
                                        selectedRatings.includes(
                                            getHotelRating(subHotel),
                                        ),
                                )
                                .map((subHotel) => {
                                    const isCurrentSubHotel =
                                        disableCurrentHotel &&
                                        subHotel.id === hotelID;

                                    return (
                                        <ListItemButton
                                            sx={{ py: 1, pl: "2em" }}
                                            disabled={isCurrentSubHotel}
                                            onClick={() => {
                                                handleToggleHotel(subHotel.id);
                                            }}
                                        >
                                            <Checkbox
                                                checked={draftSelectedHotels.includes(
                                                    subHotel.id,
                                                )}
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                onChange={() =>
                                                    handleToggleHotel(
                                                        subHotel.id,
                                                    )
                                                }
                                            />
                                            <ListItemAvatar>
                                                <Avatar
                                                    alt={`Hotel Logo ${subHotel.name}`}
                                                    src={subHotel.img ?? ""}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                secondary={
                                                    getHotelRating(subHotel) ===
                                                    null
                                                        ? subHotel.id
                                                        : `${subHotel.id} · ${getHotelRating(subHotel)}★`
                                                }
                                                primary={
                                                    isMobile
                                                        ? subHotel.name.substring(
                                                              0,
                                                              20,
                                                          ) + "..."
                                                        : subHotel.name
                                                }
                                            />
                                        </ListItemButton>
                                    );
                                })}
                        </List>
                    </Collapse>
                )}
            </>
        );
    };

    return (
        <>
            {!isExternallyControlled && (
                <Stack direction={"column"}>
                    <Typography>Select hotels</Typography>
                    <Button
                        variant="outlined"
                        sx={{
                            textTransform: "none",
                            height: ITEM_HEIGHT,
                            borderColor: Colours.midLightText,
                            "&:hover": {
                                borderColor: Colours.midLightText,
                                backgroundColor: "transparent",
                            },
                            color: Colours.textColour,
                            fontSize: "1.2rem",
                        }}
                        onClick={() => setOpenDialog(true)}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            {/* Select hotels ({selectedHotels.length}) */}
                            {/* {selectedHotels
                                .map((id) => getHotelInfo(HOTELS_INFO[id]))
                                .filter(Boolean).length > 0 && (
                                <AvatarGroup
                                    max={4}
                                    sx={{
                                        "& .MuiAvatar-root": {
                                            // width: 36,
                                            // height: 36,
                                            fontSize: "0.75rem",
                                        },
                                    }}
                                >
                                    {selectedHotels
                                        .map((id) => getHotelInfo(HOTELS_INFO[id]))
                                        .filter(Boolean)
                                        .map((hotel) => (
                                            <Avatar
                                                key={hotel.id}
                                                alt={hotel.name}
                                                src={hotel.img ?? ""}
                                                sx={{
                                                    backgroundColor: "white",
                                                }}
                                            />
                                        ))}
                                </AvatarGroup>
                            )} */}
                            {selectedHotels.length} hotel
                            {selectedHotels.length !== 1 ? "s" : ""}
                            <Icons.ArrowDropDown
                                sx={{ color: Colours.greyIcon }}
                            />
                        </Stack>
                    </Button>
                </Stack>
            )}
            <Dialog onClose={handleClickX} open={dialogOpen}>
                <DialogTitle>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                    >
                        <Typography variant="h6">Select Hotels</Typography>
                        <TextField
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icons.Search />
                                    </InputAdornment>
                                ),
                            }}
                            value={searchText}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                            variant="standard"
                        />
                        <IconButton onClick={handleClickX}>
                            <Icons.CloseNavbar />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    {showStarsFilter && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1, flexWrap: "wrap" }}
                        >
                            <Button
                                size="small"
                                variant={
                                    selectedRatings.length === 0
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => setSelectedRatings([])}
                                sx={{ textTransform: "none" }}
                            >
                                All
                            </Button>

                            {HOTEL_RATINGS.map((rating) => (
                                <Button
                                    key={rating}
                                    size="small"
                                    variant={
                                        selectedRatings.includes(rating)
                                            ? "contained"
                                            : "outlined"
                                    }
                                    onClick={() =>
                                        setSelectedRatings((prev) =>
                                            prev.includes(rating)
                                                ? prev.filter(
                                                      (r) => r !== rating,
                                                  )
                                                : [...prev, rating],
                                        )
                                    }
                                    sx={{ textTransform: "none" }}
                                >
                                    {rating}★
                                </Button>
                            ))}
                        </Stack>
                    )}
                    <List sx={{ pt: 0, maxHeight: 800 }}>
                        {AccountManager && accountManagers.length > 0 && (
                            <React.Fragment>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    onClick={() =>
                                        setExpandAccountManagers((v) => !v)
                                    }
                                    sx={{ cursor: "pointer" }}
                                >
                                    {expandAccountManagers ? (
                                        <Icons.ArrowUp />
                                    ) : (
                                        <Icons.ArrowDown />
                                    )}
                                    <Typography fontSize={"0.8em"}>
                                        Account Managers
                                    </Typography>
                                </Stack>

                                <Collapse in={expandAccountManagers}>
                                    {accountManagers
                                        .filter(
                                            (manager) =>
                                                manager.manager_name !==
                                                "OFFBOARDED",
                                        )
                                        .map((manager) => {
                                            const managerHotels =
                                                accountManagerHotelsById[
                                                    String(manager?.manager_id)
                                                ] ?? [];
                                            const visibleManagerHotels =
                                                ratingFilteredAccountManagerHotelsById[
                                                    String(manager?.manager_id)
                                                ] ?? [];
                                            const hiddenByRating =
                                                managerHotels.length -
                                                visibleManagerHotels.length;

                                            const isSelectedAccountManager =
                                                String(
                                                    selectedAccountManagerId,
                                                ) ===
                                                    String(
                                                        manager?.manager_id,
                                                    ) ||
                                                (visibleManagerHotels.length >
                                                    0 &&
                                                    visibleManagerHotels.length ===
                                                        draftSelectedHotels.length &&
                                                    visibleManagerHotels.every(
                                                        (id) =>
                                                            draftSelectedHotels.includes(
                                                                id,
                                                            ),
                                                    ));

                                            return (
                                                <ListItemButton
                                                    key={manager?.manager_id}
                                                    onClick={() =>
                                                        handleSelectAccountManager(
                                                            manager?.manager_id,
                                                        )
                                                    }
                                                >
                                                    <Checkbox
                                                        checked={
                                                            isSelectedAccountManager
                                                        }
                                                        onClick={(event) =>
                                                            event.stopPropagation()
                                                        }
                                                        onChange={() =>
                                                            handleSelectAccountManager(
                                                                manager?.manager_id,
                                                            )
                                                        }
                                                    />

                                                    <ListItemText
                                                        secondary={[
                                                            manager?.manager_email,
                                                            `${managerHotels.length} hotel${
                                                                managerHotels.length !==
                                                                1
                                                                    ? "s"
                                                                    : ""
                                                            }`,
                                                            hiddenByRating > 0
                                                                ? `${visibleManagerHotels.length} visible`
                                                                : null,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                        primary={
                                                            manager?.manager_name ||
                                                            manager?.manager_email ||
                                                            `#${manager?.manager_id}`
                                                        }
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                </Collapse>

                                <Divider
                                    sx={{
                                        borderColor: Colours.navbarBottomLine,
                                    }}
                                />

                                {/* <Typography fontSize={"0.8em"} sx={{ mt: 1 }}>
                                    Hotels
                                </Typography> */}
                            </React.Fragment>
                        )}
                        <Stack
                            direction="row"
                            alignItems="center"
                            onClick={() => setExpandHotels((v) => !v)}
                            sx={{ cursor: "pointer" }}
                        >
                            {expandHotels ? (
                                <Icons.ArrowUp />
                            ) : (
                                <Icons.ArrowDown />
                            )}
                            <Typography sx={{}} fontSize={"0.8em"}>
                                Hotels · {draftSelectedHotels.length} selected
                            </Typography>
                        </Stack>
                        <Collapse in={expandHotels}>
                            {/* No searching, so all */}
                            {searchText === "" &&
                                [...displayedOptions]
                                    .sort((a, b) => (a.name > b.name ? 1 : -1))
                                    .map((data) => (
                                        <ListItemComponent
                                            key={data.id}
                                            data={data}
                                        />
                                    ))}

                            {/* No results */}
                            {displayedOptions.length === 0 && (
                                <React.Fragment>
                                    <Divider
                                        sx={{
                                            borderColor:
                                                Colours.navbarBottomLine,
                                        }}
                                    />
                                    <Typography fontSize="0.8em">
                                        No matching hotels found.
                                    </Typography>
                                </React.Fragment>
                            )}

                            {/* Top 4 */}
                            {searchText !== "" &&
                                displayedOptions.length > 0 && (
                                    <>
                                        <React.Fragment>
                                            <Divider
                                                sx={{
                                                    borderColor:
                                                        Colours.navbarBottomLine,
                                                }}
                                            />
                                            <Typography fontSize="0.8em">
                                                Main results
                                            </Typography>
                                        </React.Fragment>

                                        {displayedOptions
                                            .slice(0, 4)
                                            .map((data) => (
                                                <ListItemComponent
                                                    key={data.id}
                                                    data={data}
                                                />
                                            ))}
                                    </>
                                )}

                            {/* Others than top 4 */}
                            {searchText !== "" &&
                                displayedOptions.length > 4 && (
                                    <>
                                        <React.Fragment>
                                            <Divider
                                                sx={{
                                                    borderColor:
                                                        Colours.navbarBottomLine,
                                                }}
                                            />
                                            <Typography fontSize={"0.8em"}>
                                                Similar results
                                            </Typography>
                                        </React.Fragment>

                                        {displayedOptions
                                            .slice(4)
                                            .map((data) => (
                                                <ListItemComponent
                                                    key={data.id}
                                                    data={data}
                                                />
                                            ))}
                                    </>
                                )}
                        </Collapse>
                    </List>
                </DialogContent>
                <Divider
                    sx={{ borderColor: Colours.midLightText, marginX: 3 }}
                />
                <DialogActions sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1} sx={{ mr: "auto" }}>
                        <Button
                            variant="outlined"
                            onClick={handleReset}
                            sx={{
                                color: "black",
                                borderColor: Colours.midLightText,
                                textTransform: "none",
                            }}
                            disabled={draftSelectedHotels.length === 0}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => handleSelectAll(true)}
                            sx={{
                                color: "black",
                                borderColor: Colours.midLightText,
                                textTransform: "none",
                            }}
                            disabled={allFilteredSelected}
                        >
                            Select all
                        </Button>
                    </Stack>
                    {onCancel && (
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            sx={{ color: "black", textTransform: "none" }}
                        >
                            {cancelLabel}
                        </Button>
                    )}
                    {onSave && (
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={effectiveSaveDisabled}
                            sx={{ textTransform: "none" }}
                        >
                            {effectiveSaveLabel}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default HotelSelectorMany;
